/**
 * API utilities for React Planner
 */

import { getApiEndpoint, ENDPOINTS } from '../config/env';

/**
 * Determines the appropriate API endpoint based on environment
 * @returns {string} API endpoint URL
 */
export function getFloorplanApiEndpoint() {
  return getApiEndpoint(ENDPOINTS.FLOORPLAN_DESIGN_API);
}

/**
 * Converts AIDesignParams to the algorithm JSON format
 * @param {Object} aiDesignParams - The AI design parameters immutable object
 * @returns {Object} Formatted algorithm JSON
 */
export function convertAIParamsToAlgoJson(aiDesignParams) {
  const algoJson = {};
  const roomCounts = aiDesignParams.get('roomCounts');
  const roomRelationships = aiDesignParams.get('roomRelationships');

  if (roomCounts === null || roomRelationships === null) {
    return {};
  }

  roomCounts.forEach((count, roomType) => {
    algoJson[roomType] = {
      num: count,
      rooms: []
    };

    for (let i = 0; i < count; i++) {
      algoJson[roomType].rooms.push({
        name: `${roomType}_${i + 1}`,
        link: [],
        location: 'northeast',
        size: 'M'
      });
    }
  });

  roomRelationships.get('connections', []).forEach((connection) => {
    const sourceRoomName = connection.get('source');
    const targetRoomName = connection.get('target');
    const sourceRoomType = sourceRoomName.split('_')[0];
    const targetRoomType = targetRoomName.split('_')[0];

    const sourceRoomTypeInfo = algoJson[sourceRoomType];
    const targetRoomTypeInfo = algoJson[targetRoomType];
    
    if (sourceRoomTypeInfo && targetRoomTypeInfo) {
      sourceRoomTypeInfo.rooms.forEach((room) => {
        if (room.name === sourceRoomName) {
          room.link.push(targetRoomName);
        }
      });

      targetRoomTypeInfo.rooms.forEach((room) => {
        if (room.name === targetRoomName) {
          room.link.push(sourceRoomName);
        }
      });
    }
  });
  
  return algoJson;
}

/**
 * 调用 FloorPlan AI Design API
 * @param {Object} state - The current state object
 * @param {Function} onSuccess - Callback function when API call succeeds
 * @param {Function} onError - Callback function when API call fails
 * @returns {Promise} - API call promise which resolves with the final result
 */
export async function callFloorplanAiDesignApi(state, onSuccess, onError) {
  try {
    // 1. 准备API调用的数据
    const timestamp = new Date().getTime();
    const exportedJson = {
      "project_name": `floorplan_${timestamp}`,
      "structure_type": "floorplan",
      "token": "aistructure",
      "algorithm": "floorplan",
      "extra_config": {
        "floorplan_outer_json": {
          "aiDesignParamsAlgoJson": convertAIParamsToAlgoJson(state.get('aiDesignParams')),
          "scene": state.get('scene').toJS()
        },
        "skip_holes_design": true
      }
    };

    // 获取API端点
    const apiUrl = getFloorplanApiEndpoint();

    // 2. 发起初始的POST请求来提交任务
    // console.log('正在调用AI设计API地址:', apiUrl);
    const initialResponse = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(exportedJson)
    });

    if (!initialResponse.ok) {
      throw new Error(`API request failed with status ${initialResponse.status}`);
    }

    const initialData = await initialResponse.json();
    // console.log('初始API响应数据 (任务已提交):', initialData);

    // 3. 构建用于查询结果的URL
    // 这个URL是根据初始响应构建的，用于后续的轮询
    const resultsUrl = apiUrl +
      `?project_name=${initialData.project_name}&attempt_id=${initialData.id}`;

    // 4. 开始轮询以获取最终结果
    // console.log('开始轮询结果, 查询URL:', resultsUrl);
    const maxTime = 15 * 60 * 1000; // 15分钟超时
    const pollInterval = 30 * 1000; // 30秒轮询间隔
    const startTime = Date.now();
    let turn = 0;

    while (Date.now() - startTime < maxTime) {
      // 等待指定的轮询间隔
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      
      turn += 1;
      // console.log(`第${turn}次轮询... 已等待 ${Math.round((Date.now() - startTime) / 1000)} 秒`);

      try {
        // 发起GET请求查询结果
        const pollResponse = await fetch(resultsUrl);

        if (!pollResponse.ok) {
          // 如果单次请求失败，打印错误但继续轮询，而不是中断整个过程
          // console.error(`轮询请求失败，状态码: ${pollResponse.status}。将在下次继续尝试。`);
          continue; 
        }

        const resultData = await pollResponse.json();
        // 首先检查返回的是否是一个非空数组
        if (Array.isArray(resultData) && resultData.length > 0) {
          const firstResult = resultData[0]; // 取出数组的第一个元素
          // 检查第一个元素中是否包含我们期望的结果数据
          if (firstResult && firstResult.output && firstResult.output.floorplan_designed_json) {
            // console.log('成功获取结果:', firstResult);
            
            if (onSuccess) {
              onSuccess(firstResult.output.floorplan_designed_json);
            }
            
            return firstResult.output.floorplan_designed_json;
          }
        }

        // 如果不满足条件，打印日志
        // console.log(`结果尚未生成，当前返回结果:`, resultData); // <-- 修改日志打印方式以获得更清晰的输出

      } catch (pollError) {
        // 捕获轮询过程中的网络错误等，打印并继续
        // console.error('轮询过程中出错，将在下次继续尝试:', pollError);
      }
    }

    // 5. 如果循环结束仍未获取结果，则抛出超时错误
    throw new Error('获取结果超时 (15分钟)');

  } catch (error) {
    // 捕获整个过程中的任何错误 (包括初始请求、超时等)
    console.error('调用 FloorPlan AI Design API 时发生错误:', error);
    if (onError) {
      onError(error);
    }
    // 将错误再次抛出，以便调用者可以捕获它
    throw error;
  }
}